import type { PayloadAction } from '@reduxjs/toolkit';
import { replaceWaitlistEntries, setWaitlistOffer } from 'reducers/waitlist';
import { mapWaitlistBroadcast, mapWaitlistNotification } from 'types/waitlist';
import {
  summarizeForWaitlistTimerLog,
  summarizeWaitlistEntryForTimerLog,
  summarizeWaitlistTimerFieldsForLog,
  waitlistTimerLog
} from 'utils/waitlistTimerDebug';

type ReplaceAction = ReturnType<typeof replaceWaitlistEntries>;
type OfferAction = ReturnType<typeof setWaitlistOffer>;

/**
 * Normalise the many wire shapes for `broadcastedWaitlist` into the
 * `{ players: [] }` shape our mapper expects, then return the dispatchable
 * action. Factored out of App.tsx so it can be unit-tested without mounting
 * the full app tree.
 *
 * Accepted shapes (in priority order):
 *   - `{ players: [...] }`              ← engine's canonical broadcast
 *   - `{ response: { players: [...] } }` ← when the envelope is wrapped
 *   - `[...]`                            ← raw array fallback
 */
export const buildBroadcastedWaitlistAction = (
  decoded: unknown,
  playerGuid: string,
  meta: { version?: number; updatedAt?: number } = {}
): ReplaceAction => {
  const input =
    decoded && typeof decoded === 'object' && 'players' in (decoded as object)
      ? (decoded as object)
      : decoded &&
        typeof decoded === 'object' &&
        'response' in (decoded as object) &&
        typeof (decoded as { response?: unknown }).response === 'object' &&
        (decoded as { response?: { players?: unknown } }).response?.players
      ? (decoded as { response: object }).response
      : Array.isArray(decoded)
      ? { players: decoded }
      : { players: [] };
  const rawPlayers = Array.isArray((input as { players?: unknown }).players)
    ? ((input as { players: unknown[] }).players as unknown[])
    : [];
  waitlistTimerLog('broadcast:timer-fields:raw', {
    playerGuid,
    entryCount: rawPlayers.length,
    entries: rawPlayers.map((entry) => summarizeWaitlistTimerFieldsForLog(entry))
  });
  const entries = mapWaitlistBroadcast(input);
  waitlistTimerLog('broadcast:normalized', {
    playerGuid,
    input: summarizeForWaitlistTimerLog(input),
    entryCount: entries.length,
    entries: entries.map(summarizeWaitlistEntryForTimerLog)
  });
  waitlistTimerLog('broadcast:timer-fields:normalized', {
    playerGuid,
    entryCount: entries.length,
    entries: entries.map(summarizeWaitlistTimerFieldsForLog)
  });
  return replaceWaitlistEntries({
    playerGuid,
    entries,
    version: meta.version,
    updatedAt: meta.updatedAt
  });
};

/**
 * Map a `gotWaitListNotification` payload to a dispatchable action, or null
 * if the payload is unrecognised (missing gameId / unknown gameType).
 */
export const buildWaitlistNotificationAction = (
  decoded: unknown
): PayloadAction<ReturnType<typeof mapWaitlistNotification>> | null => {
  const notif = mapWaitlistNotification(decoded);
  waitlistTimerLog(notif ? 'notification:normalized' : 'notification:normalization-failed', {
    decoded: summarizeForWaitlistTimerLog(decoded),
    notification: notif
  });
  if (!notif) return null;
  return setWaitlistOffer(notif) as OfferAction;
};
